import { useEffect, useRef } from 'react'
import { captureException, setUser } from '@sentry/nextjs'

import { refreshToken, refreshFirebaseToken } from '~/services/axiosInstance.service'
import { execute, getFirebase } from '~/firebase'
import {
  setFirebaseAuthenticated,
  selectJwtExpiresInSeconds,
  isAuthenticated,
  selectUserId,
  tokenReceived,
  updateToken,
  logoutUser,
  updateFirebaseToken,
} from '~/auth/slices/auth.slice'
import { useLazyGetMeQuery } from '~/redux/apis/user-api'
import { selectCurrentUser } from '~/redux/slices/user'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import authService from '~/services/auth.service'
import accountAnalytics from '~/analytics/account/accountAnalytics'

const useAuth = (): void => {
  const authenticated = useAppSelector(isAuthenticated)
  const dispatch = useAppDispatch()
  const userId = useAppSelector(selectUserId)
  const user = useAppSelector(selectCurrentUser)

  const jwtExpiresInSeconds = useAppSelector(selectJwtExpiresInSeconds)
  const [fetchMe, { isLoading, isError }] = useLazyGetMeQuery()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // initializes redux storage from localstorage
  useEffect(() => {
    const accessToken = authService.getAccessToken()
    const firebaseToken = authService.getFirebaseToken()
    if (accessToken && firebaseToken) {
      dispatch(tokenReceived({ accessToken, firebaseToken }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // if authenticated fetch user
  useEffect(() => {
    if (authenticated) {
      if (!isError && !isLoading && (!user || user.id !== userId)) {
        fetchMe()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, user, fetchMe, isError, isLoading, userId])

  // set sentry user context
  useEffect(() => {
    if (user?.id) {
      setUser({ id: String(user.id) })
    } else {
      setUser(null)
    }
  }, [user?.id])

  // eager token refresh
  useEffect(() => {
    const clearTimer = (): void => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    const refresh = async (): Promise<void> => {
      try {
        const refreshResult = await refreshToken()
        if (refreshResult.data && refreshResult.data.accessToken) {
          dispatch(updateToken(refreshResult.data))
          accountAnalytics.logAuthenticateWithToken(user?.log)
        } else {
          dispatch(logoutUser())
        }
      } catch (error) {
        captureException(error, { tags: { feature: 'auth', action: 'token-refresh' } })
      }
    }

    if (jwtExpiresInSeconds) {
      const expiresAt = jwtExpiresInSeconds * 1000
      const mlsIn5Minutes = 5 * 60 * 1000
      const refreshTokenAt = expiresAt - mlsIn5Minutes
      const now = new Date().getTime()
      const runTimerIn = refreshTokenAt > now ? refreshTokenAt - now : 0

      // console.warn(runTimerIn > 0 ? 'REFRESH LATER' : 'REFRESH NOW')
      if (timerRef.current) {
        clearTimer()
      }
      timerRef.current = setTimeout(refresh, runTimerIn)
      return () => clearTimer()
    }
  }, [jwtExpiresInSeconds, dispatch, user?.log])

  // manage firebase authentication status
  useEffect(() => {
    if (!authenticated) return

    const refreshFirebase = async (): Promise<void> => {
      try {
        const res = await refreshFirebaseToken()
        if (res.data && 'firebaseToken' in res.data) {
          dispatch(updateFirebaseToken(res.data.firebaseToken))
        }
      } catch (error) {
        captureException(error, { tags: { feature: 'auth', action: 'firebase-refresh' } })
      }
    }

    const run = async (): Promise<() => void> => {
      const { onAuthStateChanged, auth } = await getFirebase()

      const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        if (firebaseUser) {
          dispatch(setFirebaseAuthenticated(true))
        } else {
          dispatch(setFirebaseAuthenticated(false))
          refreshFirebase()
        }
      })

      return () => {
        unsubscribe()
      }
    }

    const cleanup = run()
    return () => {
      execute(cleanup)
    }
  }, [dispatch, authenticated])
}

export default useAuth
